import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

// 1. Define the context type
interface SidebarContextType {
  isExpanded: boolean;
  setIsExpanded: (value: boolean) => void;
  sidebarWidth: string;
  isMobile: boolean;
}

// 2. Export the context
export const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

// 3. Define provider props
interface SidebarProviderProps {
  children: ReactNode;
}

// 4. Create the provider component
export const SidebarProvider = ({ children }: SidebarProviderProps): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile view
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile(); // initial check
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Sidebar width logic
  const sidebarWidth = isMobile ? '0px' : isExpanded ? '250px' : '60px';

  return (
    <SidebarContext.Provider value={{ isExpanded, setIsExpanded, sidebarWidth, isMobile }}>
      {children}
    </SidebarContext.Provider>
  );
};

// 5. Custom hook
export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
